// src/components/ProductCard.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toggleWishlist } from '../firebase/services';

const HeartIcon = ({ filled }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);
const CartIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <path d="M16 10a4 4 0 0 1-8 0"/>
  </svg>
);

export default function ProductCard({ product, wishlistIds = [], onWishlistChange, animDelay = 0 }) {
  const { addToCart } = useCart();
  const { user, refreshUserData } = useAuth();
  const [hovered, setHovered] = useState(false);
  const [added, setAdded] = useState(false);
  const [wishToggeling, setWishToggling] = useState(false);

  const isFood = product.type === 'food';
  const isWishlisted = wishlistIds.includes(product.id);
  const stars = '★'.repeat(Math.round(product.rating || 0)) + '☆'.repeat(5 - Math.round(product.rating || 0));
  const discount = product.originalPrice
    ? Math.round((1 - product.price / product.originalPrice) * 100) : null;

  const handleAdd = (e) => {
    e.preventDefault(); e.stopPropagation();
    addToCart({
      ...product,
      selectedSize: product.sizes?.[0] || null,
      selectedColor: product.colors?.[0] || null,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1600);
  };

  const handleWishlist = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user || wishToggeling) return;
    setWishToggling(true);
    try {
      await toggleWishlist(user.uid, product.id);
      await refreshUserData();
      if (onWishlistChange) onWishlistChange();
    } finally {
      setWishToggling(false);
    }
  };

  return (
    <Link to={`/product/${product.id}`} style={{ textDecoration: 'none', display: 'block', animationDelay: `${animDelay}s` }} className="anim-up">
      <div
        className="card"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          cursor: 'pointer',
          transform: hovered ? 'translateY(-5px)' : 'none',
          boxShadow: hovered ? '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(212,168,83,0.15)' : 'none',
          borderColor: hovered ? 'var(--border-2)' : 'var(--border)',
          transition: 'all 0.35s var(--ease)',
        }}
      >
        {/* Image */}
        <div style={{ position: 'relative', overflow: 'hidden', paddingTop: isFood ? '72%' : '118%', background: 'var(--surface-2)' }}>
          <img
            src={product.image}
            alt={product.name}
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover',
              transform: hovered ? 'scale(1.07)' : 'scale(1)',
              transition: 'transform 0.6s var(--ease)',
            }}
          />
          {/* Gradient overlay */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to top, rgba(9,12,10,0.7) 0%, transparent 50%)',
            opacity: hovered ? 1 : 0.4,
            transition: 'opacity 0.35s',
          }} />

          {/* Badges */}
          <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span className={`badge ${isFood ? 'badge-emerald' : 'badge-gold'}`}>
              {isFood ? '🍽 Food' : '🛍 Shop'}
            </span>
            {discount && <span className="badge badge-gold">-{discount}%</span>}
            {product.featured && <span className="badge badge-gold">✦ Featured</span>}
          </div>

          {/* Wishlist btn */}
          {user && (
            <button
              onClick={handleWishlist}
              style={{
                position: 'absolute', top: 12, right: 12,
                width: 34, height: 34, borderRadius: '50%',
                background: isWishlisted ? 'var(--gold)' : 'rgba(9,12,10,0.7)',
                border: `1px solid ${isWishlisted ? 'var(--gold)' : 'var(--border)'}`,
                color: isWishlisted ? '#09080A' : 'var(--text-2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', backdropFilter: 'blur(8px)',
                transform: hovered ? 'translateX(0)' : 'translateX(50px)',
                opacity: hovered ? 1 : 0,
                transition: 'all 0.3s var(--ease)',
              }}
            >
              <HeartIcon filled={isWishlisted} />
            </button>
          )}

          {/* Food extras */}
          {isFood && (
            <div style={{ position: 'absolute', bottom: 10, left: 12, display: 'flex', gap: 8 }}>
              {product.prepTime && <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.8)', background: 'rgba(0,0,0,0.5)', padding: '2px 8px', borderRadius: 10, backdropFilter: 'blur(4px)' }}>⏱ {product.prepTime}</span>}
              {product.isVeg && <span style={{ fontSize: 10, color: '#74C69D', background: 'rgba(0,0,0,0.5)', padding: '2px 8px', borderRadius: 10, backdropFilter: 'blur(4px)' }}>🟢 Veg</span>}
            </div>
          )}

          {/* Add to cart - slide up on desktop, always visible on mobile */}
          <div
            className="add-to-cart-slide"
            style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              transform: hovered ? 'translateY(0)' : 'translateY(100%)',
              transition: 'transform 0.35s var(--ease)',
            }}>
            <button
              onClick={handleAdd}
              style={{
                width: '100%', padding: '13px', border: 'none',
                background: added
                  ? 'rgba(76,175,125,0.9)'
                  : isFood ? 'rgba(45,106,79,0.92)' : 'rgba(212,168,83,0.92)',
                color: added ? 'white' : isFood ? 'white' : '#09080A',
                fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                backdropFilter: 'blur(10px)',
                transition: 'background 0.3s',
              }}
            >
              {added ? '✓ Added to Bag' : <><CartIcon /> {isFood ? 'Order Now' : 'Add to Bag'}</>}
            </button>
          </div>
        </div>

        {/* Info */}
        <div style={{ padding: 'clamp(10px,2vw,18px) clamp(10px,2vw,16px) clamp(12px,2vw,20px)' }}>
          <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 5 }}>
            {product.category}
          </div>
          <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(13px,2vw,16px)', fontWeight: 400, marginBottom: 8, lineHeight: 1.3 }}>
            {product.name}
          </h3>

          {product.rating > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <span className="stars" style={{ fontSize: 11 }}>{stars}</span>
              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>({product.reviewCount})</span>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(16px,2.5vw,20px)', color: 'var(--gold)' }}>
                ${product.price}
              </span>
              {product.originalPrice && (
                <span style={{ fontSize: 13, color: 'var(--text-3)', textDecoration: 'line-through' }}>${product.originalPrice}</span>
              )}
            </div>
            {isFood && product.calories && (
              <span style={{ fontSize: 10, color: 'var(--text-3)' }}>{product.calories} cal</span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
